import { Injectable, computed, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ErrorStateService, HttpErrorResponseLike } from './error-state.service';
import { GmrFunction } from './gmr.models';
import { sanitisedDownloadUrl } from './matomo.service';
import { ThirdPartyIntegrationsService } from './third-party-integrations.service';

//
// The states an entry passes through, and which of them are final. Terminal entries stay in the
// register as the record of what was asked for and what came of it - see DownloadService below.
//
export type DownloadState =
  | 'waiting'
  | 'requested'
  | 'receiving'
  | 'received'
  | 'failed'
  | 'cancelled'
  | 'uploading'
  | 'unknown';

const TERMINAL_STATES: readonly DownloadState[] = ['received', 'failed', 'cancelled', 'unknown'];

export type DownloadRoute = 'fetch' | 'native';

export interface DownloadEntry
{
  readonly id: number;
  readonly presetName: string;

  //
  // The resource code of the report's own description, so the row names itself in the user's
  // language from the same code as the heading above the form (UI-4).
  //
  readonly titleCode: string;

  //
  // The serialised form, i.e. the exact string HashStateService.writeBookmark() puts in the bookmark
  // hash. It is what makes two runs of one report distinguishable, which is the whole point of a
  // register rather than a single state (see design F-1).
  //
  readonly requestSummary: string;

  readonly route: DownloadRoute;
  readonly state: DownloadState;

  //
  // Bytes received and, where the server declared a Content-Length, the total. Absent totals are the
  // normal case for a streamed answer, and then the indicator stays indeterminate rather than
  // inventing a percentage (design S-21).
  //
  readonly receivedBytes: number;
  readonly totalBytes: number | null;

  readonly fileName: string | null;

  //
  // Set on 'failed' only, and the `kind` is the whole of the difference between the two:
  //
  // - 'answer'  - the server answered and the answer was not a file. There is a status and a body, so
  //               ErrorStateService.showFromParseFailure() can describe it, and offering the detail is
  //               worth it.
  // - 'network' - the request never completed: no status, no headers, no body. `reason` is the only
  //               thing known and it comes from the browser ("Failed to fetch", "Load failed", ...).
  //               There is nothing for the error modal to show, which is why no detail is offered -
  //               the first build offered it anyway and rendered "HTTP 0, body is not JSON", a
  //               sentence about a JSON body that was never received, sending the reader after a
  //               problem that does not exist.
  //
  readonly failure: { kind: 'answer' | 'network'; status: number; url: string; bodyExcerpt: string | null; reason: string | null } | null;

  //
  // A line of plain text under the state, where something happened to this download that the state
  // alone does not carry - today only the hand-over to the browser described in fallBackToBrowser().
  //
  readonly note: string | null;
}

//
// Decided by the maintainer on 2026-08-14 (design D-8): three DIFFERENT reports may execute at once.
// With an export of at most 10 MB (design D-4) that bounds what the page holds at roughly 60 MB.
//
const MAX_CONCURRENT = 3;

//
// How much of a non-attachment answer is read back to show the user what arrived instead of a file.
// An HTML login page, a gateway notice and a truncated JSON document are all recognisable from their
// first line; reading the whole body would mean buffering a page to describe it.
//
const FAILURE_BODY_LIMIT = 4096;

//
// The register of downloads, and the two routes that feed it.
//
// Replaces the fixed 5s message + 2s spinner in LoadingService, which was an honest sequence built on
// no signal at all, and could only ever describe ONE download: showDownloadProgress() opened by
// clearing whatever was there, so a second submit erased the first download's record (design F-1).
//
// Two routes, and the choice is one field - cfn_form_method (design D-5):
//
//   fetch  - the 55 GET reports. fetch() resolves when the response HEADERS arrive, which is when the
//            server starts sending, so 'requested' -> 'receiving' is a real event. Reading the body to
//            the end is a second real event, 'received'. What is NOT knowable is whether the browser
//            then wrote the file: after the object URL is handed over the page is told nothing, so
//            nothing here ever claims the file is saved (design D-1).
//   native - the 11 POST reports, uploads included. A native submit is a navigation, so the browser
//            reports the upload itself with a percentage this code cannot obtain; adding a second,
//            vaguer indicator beside it would compete with a better one (design A-7, D-12). These get
//            an entry, because an entry carries the overview, and no progress indicator at all.
//
@Injectable({ providedIn: 'root' })
export class DownloadService
{
  private readonly translate = inject(TranslateService);
  private readonly errorState = inject(ErrorStateService);
  private readonly thirdParty = inject(ThirdPartyIntegrationsService);

  private readonly entriesSignal = signal<DownloadEntry[]>([]);

  //
  // Newest first: the row a user is looking for is the one they just started.
  //
  readonly entries = this.entriesSignal.asReadonly();

  readonly unfinishedCount = computed(() => this.entriesSignal().filter(e => !isTerminal(e.state)).length);
  readonly hasUnfinished = computed(() => this.unfinishedCount() > 0);

  //
  // Announced to a screen reader, and only for a transition that ended something. Announcing every
  // byte, or every state, would be continuous speech with three downloads running (design S-23).
  //
  readonly announcement = signal('');

  //
  // Something to SAY, on screen, when a submit did not produce a new download. Added 2026-08-14 after
  // the first build made the button look dead: a refused duplicate wrote only into the live region
  // above, which carries clip-path: inset(50%) - so a screen-reader user was told and everyone else
  // saw precisely nothing happen. That is the fault this whole design exists to remove (design C-3),
  // reproduced in the code that removes it.
  //
  readonly notice = signal<string>('');

  //
  // The entry a notice is about, so the list can point at it rather than leaving the user to work out
  // which of several rows is meant.
  //
  readonly highlightedId = signal<number | null>(null);

  //
  // Raised every time an entry is added, so the list can bring itself into view. It is a counter
  // rather than a boolean because two submits in a row have to be two events.
  //
  readonly revealCounter = signal(0);

  dismissNotice(): void
  {
    this.notice.set('');
    this.highlightedId.set(null);
  }

  private nextId = 1;
  private readonly controllers = new Map<number, AbortController>();

  //
  // Started but not yet begun: the queue. Kept as thunks rather than as a list of ids so that a
  // cancelled entry simply never runs, without a second bookkeeping structure to keep in step.
  //
  private readonly queued = new Map<number, () => void>();

  //
  // Returns false when the submit was refused, in which case the caller must not let a native submit
  // proceed either. Refusal happens for an identical request that is already running (design S-13):
  // running it twice yields the same file, and there is nothing to queue.
  //
  start(fn: GmrFunction, form: HTMLFormElement, requestSummary: string): boolean
  {
    const isPost = (fn.cfn_form_method ?? 'GET').toUpperCase() === 'POST';
    const route: DownloadRoute = isPost ? 'native' : 'fetch';

    this.dismissNotice();

    const duplicate = this.findDuplicate(fn.cfn_preset_name, requestSummary);
    if (duplicate)
    {
      //
      // Refused rather than queued: running it again yields the same file, and for a report that
      // writes it would post twice. What must NOT happen is refusing in silence - the user pressed a
      // button and is owed an answer, which is why this says so on screen, points at the entry it
      // means, and brings the list into view.
      //
      const text = this.translate.instant('itgen_gmr_download_already_running');
      this.announcement.set(text);
      this.notice.set(text);
      this.highlightedId.set(duplicate.id);
      this.revealCounter.update(n => n + 1);
      return false;
    }

    const entry: DownloadEntry =
    {
      id: this.nextId++
    , presetName: fn.cfn_preset_name
    , titleCode: fn.cfn_description
    , requestSummary
    , route
    , state: route === 'native' ? (isFileUpload(fn) ? 'uploading' : 'unknown') : 'waiting'
    , receivedBytes: 0
    , totalBytes: null
    , fileName: null
    , failure: null
    , note: null
    };

    this.entriesSignal.update(entries => [entry, ...entries]);
    this.revealCounter.update(n => n + 1);

    //
    // Reported here rather than left to Matomo's own download detection, which cannot see any of
    // this: that detection hooks clicks on <a href> elements, and neither route below is one. A GET
    // report is fetched from JavaScript and a POST report is submitted natively into a hidden
    // iframe, so to a click listener nothing whatsoever happened.
    //
    // The start, not the finish, is what gets the trackLink call, matching what Matomo counts for an
    // ordinary download link: the user asked for the file. How it ended is a separate event, raised
    // in patch() below.
    //
    this.thirdParty.trackDownload(sanitisedDownloadUrl(fn.cfn_preset_name), fn.cfn_preset_name);

    if (route === 'native')
    {
      //
      // Each native download gets its own iframe. Sharing one - which every form did until this
      // change - means the second submit navigates the same browsing context and drops a first
      // request that is still generating, losing a download with no message anywhere (design F-2).
      //
      form.target = this.createDownloadFrame(entry.id);
      return true;
    }

    const action = new URL(form.getAttribute('action') ?? 'Preset', document.baseURI);
    const query = new URLSearchParams(Array.from(new FormData(form).entries()) as [string, string][]);
    const url = action.toString() + '?' + query.toString();

    this.enqueue(entry.id, entry.presetName, () => void this.run(entry.id, url));
    return true;
  }

  //
  // Two gates, and they are different rules for different reasons (design S-14, D-8):
  //
  // - one execution per preset, because a report builds its intermediate results in temporary tables
  //   in @inmemorystorage which are session-scoped, so the same template run twice replaces its own
  //   tables under itself and the FIRST run comes back wrong. Commit ee6a272 made those names unique
  //   per template, which is what makes gating per preset sufficient rather than half a fix;
  // - three executions in total, which is memory and backend load.
  //
  private enqueue(id: number, presetName: string, run: () => void): void
  {
    this.queued.set(id, run);
    this.pump();
  }

  private pump(): void
  {
    for (const [id, run] of Array.from(this.queued.entries()).reverse())
    {
      const entry = this.find(id);
      if (!entry)
      {
        this.queued.delete(id);
        continue;
      }

      if (this.executingCount() >= MAX_CONCURRENT) return;
      if (this.isPresetExecuting(entry.presetName)) continue;

      this.queued.delete(id);
      run();
    }
  }

  private async run(id: number, url: string): Promise<void>
  {
    const controller = new AbortController();
    this.controllers.set(id, controller);
    this.patch(id, { state: 'requested' });

    let response: Response;
    try
    {
      response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
    }
    catch (error)
    {
      this.controllers.delete(id);
      if (controller.signal.aborted) return;

      //
      // fetch rejects with a bare TypeError and nothing else: no status, no URL, no body. The likeliest
      // cause of one here is a session that expired mid-download, because the redirect to the
      // authenticator is cross-origin and therefore unreadable (design F-21). Asking the auth endpoint
      // settles it, and it is asked with fetch rather than HttpClient on purpose: the global
      // errorInterceptor sets a module-level "suppress every further request" flag for the statuses it
      // handles, which is a fair reaction to a broken catalogue and a disastrous one for one failed
      // report out of three (design F-22).
      //
      await this.reportNetworkFailure(id, url, describe(error));
      this.pump();
      return;
    }

    const disposition = response.headers.get('Content-Disposition') ?? '';
    const contentType = response.headers.get('Content-Type') ?? '';
    const isAttachment = /attachment/i.test(disposition);

    if (!response.ok)
    {
      this.controllers.delete(id);
      await this.reportAnswerFailure(id, url, response);
      this.pump();
      return;
    }

    //
    // The answer did not come from where it was asked for. That single fact is what separates the two
    // cases a 200 carrying HTML can be: an expired session or a gateway putting its own page in the
    // way, which is wrong and worth reporting, from a report whose answer simply IS a document, which
    // is right. Reading the content type cannot tell them apart - measured over the presets on
    // 2026-08-14, 35 of them declare `Format=Xlsx` but three declare `Html`, three `Data` and one
    // `Xml`, so HTML is a legitimate answer for some reports and an error page for the rest.
    //
    // A Response that carries no url of its own - a synthetic one, or anything a service worker
    // constructed - says nothing about where the answer came from, and must not be read as a bounce.
    if (response.redirected || (!!response.url && stripQuery(response.url) !== stripQuery(url)))
    {
      this.controllers.delete(id);
      if (!await this.hasSessionExpired())
      {
        await this.reportAnswerFailure(id, url, response);
      }
      this.pump();
      return;
    }

    //
    // Not declared an attachment and textual: this is one of the reports whose answer is a document
    // rather than a file. Handing it to the browser reproduces exactly what a native submit did with
    // it before this route existed, which is the only behaviour known to be right for them - and
    // saying "failed" here would have failed four working reports.
    //
    if (!isAttachment && isTextualType(contentType))
    {
      this.controllers.delete(id);
      this.fallBackToBrowser(id, url, this.translate.instant('itgen_gmr_download_reason_not_a_file'));
      this.pump();
      return;
    }

    const declaredLength = Number(response.headers.get('Content-Length'));
    const totalBytes = Number.isFinite(declaredLength) && declaredLength > 0 ? declaredLength : null;
    this.patch(id, { state: 'receiving', totalBytes });

    try
    {
      const blob = await this.readBody(id, response, totalBytes);
      this.controllers.delete(id);

      //
      // Last guard, and the one that matters most: the expected answer is a file - an XLSX for 35 of
      // the presets, a ZIP or a document for the rest - and every one of those begins with bytes that
      // are not text. An answer that claims a binary content type and starts with `<` or `{` is an
      // error document wearing the wrong header, and delivering it would hand the user a file called
      // `<report>.xlsx` that Excel refuses to open. The excerpt says what actually arrived.
      //
      if (!isAttachment)
      {
        const opening = await readOpening(blob);
        if (looksLikeDocument(opening))
        {
          this.patch(id,
          {
            state: 'failed'
          , failure: { kind: 'answer', status: response.status, url, bodyExcerpt: opening.replace(/\s+/g, ' ').trim().slice(0, 200) || null, reason: null }
          });
          this.announce('itgen_gmr_download_announced_failed', id);
          this.pump();
          return;
        }
      }

      const fileName = fileNameFrom(disposition) ?? this.find(id)?.presetName + '.xlsx';
      this.deliver(blob, fileName);
      this.patch(id, { state: 'received', fileName });
      this.announce('itgen_gmr_download_announced_received', id);
    }
    catch (error)
    {
      this.controllers.delete(id);
      if (!controller.signal.aborted)
      {
        await this.reportNetworkFailure(id, url, describe(error));
      }
    }

    this.pump();
  }

  //
  // Read through a reader when a length was declared, so the row can count real bytes; otherwise take
  // the blob in one go. Progress is written at most every 200 ms: with three downloads streaming, a
  // signal updated per chunk re-renders the list thousands of times a second for no gain (design
  // F-23).
  //
  private async readBody(id: number, response: Response, totalBytes: number | null): Promise<Blob>
  {
    if (totalBytes == null || !response.body)
    {
      return await response.blob();
    }

    const reader = response.body.getReader();
    const chunks: BlobPart[] = [];
    let received = 0;
    let lastReport = 0;

    for (;;)
    {
      const { done, value } = await reader.read();
      if (done) break;
      if (!value) continue;

      chunks.push(value as unknown as BlobPart);
      received += value.byteLength;

      const now = performance.now();
      if (now - lastReport > 200)
      {
        lastReport = now;
        this.patch(id, { receivedBytes: received });
      }
    }

    this.patch(id, { receivedBytes: received });
    return new Blob(chunks, { type: response.headers.get('Content-Type') ?? 'application/octet-stream' });
  }

  //
  // Hand the bytes to the browser. Revoking on the next task rather than immediately is not
  // superstition: revoking before the browser has taken the blob cancels the save.
  //
  private deliver(blob: Blob, fileName: string): void
  {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    link.rel = 'noopener';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    setTimeout(() =>
    {
      URL.revokeObjectURL(objectUrl);
      link.remove();
    }, 0);
  }

  //
  // Abandon the request and let the server finish on its own (design D-9). A queued entry has sent
  // nothing, so it is simply removed; a running one becomes 'cancelled', whose wording says the
  // receiving was stopped rather than that the report was called off - because it very probably ran
  // to completion server-side and nothing here can tell.
  //
  cancel(id: number): void
  {
    const entry = this.find(id);
    if (!entry || isTerminal(entry.state)) return;

    if (this.queued.delete(id))
    {
      this.remove(id);
      this.pump();
      return;
    }

    this.controllers.get(id)?.abort();
    this.controllers.delete(id);
    this.patch(id, { state: 'cancelled' });
    this.pump();
  }

  remove(id: number): void
  {
    this.queued.delete(id);
    this.entriesSignal.update(entries => entries.filter(e => e.id !== id));
  }

  clearFinished(): void
  {
    this.entriesSignal.update(entries => entries.filter(e => !isTerminal(e.state)));
  }

  //
  // Open the existing error modal for one failed entry, on demand rather than automatically.
  // errorModal is a single signal holding one error, so two failures would overwrite each other -
  // the same single-state fault the register exists to remove (design S-11).
  //
  //
  // Only ever called for a failure of kind 'answer'; the list offers no detail for the other kind,
  // because there is no answer to describe. Guarded here as well rather than trusting the template.
  //
  showFailure(id: number): void
  {
    const entry = this.find(id);
    if (entry?.failure?.kind !== 'answer') return;

    const like: HttpErrorResponseLike =
    {
      status: entry.failure.status
    , url: entry.failure.url
    , error: { text: entry.failure.bodyExcerpt }
    };

    //
    // instant(), for the reason announce() gives: a callback from outside the Angular zone writes state
    // that no render is scheduled for, so the modal would open one user action late.
    //
    this.errorState.showFromParseFailure(
      like
    , 'xxgmr121'
    , this.translate.instant('itgen_gmr_error')
    , this.translate.instant('itgen_gmr_invalid_answer_from_server'));
  }

  private async reportAnswerFailure(id: number, url: string, response: Response): Promise<void>
  {
    let bodyExcerpt: string | null = null;
    try
    {
      const text = await response.text();
      bodyExcerpt = text.slice(0, FAILURE_BODY_LIMIT).replace(/\s+/g, ' ').trim() || null;
    }
    catch
    {
      bodyExcerpt = null;
    }

    this.patch(id, { state: 'failed', failure: { kind: 'answer', status: response.status, url, bodyExcerpt, reason: null } });
    this.announce('itgen_gmr_download_announced_failed', id);
  }

  //
  // The request never completed. Reported by the browser as a bare TypeError with a one-line message
  // and nothing else - no status, no headers, no body - so `reason` is the whole of the evidence and
  // it goes to the console as well, because that is where the next person will look.
  //
  // What happens next is a hand-over rather than an error. Observed in production on 2026-08-14: a
  // report that downloads perfectly well through a plain browser navigation can fail at the network
  // layer under fetch, and the design already knew one way that happens - an endpoint that redirects
  // cross-origin is unreadable to fetch and unremarkable to a navigation (design F-21). Whatever the
  // cause, the browser can still fetch it the way it always did, so it is asked to.
  //
  private async reportNetworkFailure(id: number, url: string, reason: string): Promise<void>
  {
    console.error('xxgmr121: the page could not fetch ' + url + ' - ' + reason);

    if (await this.hasSessionExpired())
    {
      return;
    }

    this.fallBackToBrowser(id, url, reason);
  }

  //
  // Hand the download to the browser as an ordinary navigation into a hidden iframe: the mechanism
  // this route replaced, and the one that demonstrably works against this endpoint.
  //
  // Safe to re-issue, and only here. Design D-2 forbids re-issuing a request after it has run,
  // because a second execution of a report that writes posts twice - but this path is reached only
  // when the request did NOT complete, and it carries only the GET reports, since every POST report
  // stays on the native submit from the start (design D-5). So the worst case is a read executed
  // twice on the server, which costs time and changes nothing.
  //
  // The entry becomes 'unknown', which is the honest state for a native download: the page cannot see
  // whether it arrives. The note says why it ended up there, so a silent downgrade does not look like
  // a success.
  //
  private fallBackToBrowser(id: number, url: string, reason: string): void
  {
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('tabindex', '-1');
    frame.style.display = 'none';
    frame.src = url;
    document.body.appendChild(frame);

    this.patch(id,
    {
      state: 'unknown'
    , note: this.translate.instant('itgen_gmr_download_note_fallback', { reason })
    , failure: { kind: 'network', status: 0, url, bodyExcerpt: null, reason }
    });
  }

  //
  // Asked once per failure and answered by navigating away, exactly as GmrDataService.startFunction()
  // does when the catalogue is refused. Returns true when the page is leaving, in which case there is
  // no point marking anything.
  //
  private async hasSessionExpired(): Promise<boolean>
  {
    try
    {
      const probe = await fetch('auth?preset=gmr-functions&returnUrl=' + encodeURIComponent(window.location.href), { credentials: 'same-origin' });
      if (!probe.ok) return false;

      const answer = await probe.json() as { isAuthenticated?: boolean; authenticationUrl?: string };
      if (answer.isAuthenticated === false && answer.authenticationUrl)
      {
        window.location.href = answer.authenticationUrl;
        return true;
      }
    }
    catch
    {
      return false;
    }
    return false;
  }

  private createDownloadFrame(id: number): string
  {
    const name = 'downloadTargetIframe-' + id;
    const frame = document.createElement('iframe');
    frame.name = name;
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('tabindex', '-1');
    frame.style.display = 'none';
    document.body.appendChild(frame);
    return name;
  }

  //
  // Deliberately translate.instant() and not translate.get().subscribe(), which is what this did until
  // 2026-08-14 and which quietly did not work. This application runs with zone-based change detection,
  // and @ngx-translate emits from outside the Angular zone: a signal written there does not schedule a
  // render, so the text sat in the signal and reached the screen only when something else happened to
  // trigger change detection - one user action late, or never. It cost an afternoon to find because the
  // state was right and only the view was stale. Anything written to a signal from a callback that is
  // not the caller's own turn has this problem; translations are already loaded by the time a download
  // is started, so asking for them synchronously has no downside here.
  //
  private announce(code: string, id: number): void
  {
    const entry = this.find(id);
    if (!entry) return;

    const title = this.translate.instant(entry.titleCode);
    this.announcement.set(this.translate.instant(code, { report: title }));
  }

  private findDuplicate(presetName: string, requestSummary: string): DownloadEntry | undefined
  {
    return this.entriesSignal().find(e =>
      e.presetName === presetName && e.requestSummary === requestSummary && !isTerminal(e.state));
  }

  private isPresetExecuting(presetName: string): boolean
  {
    return this.entriesSignal().some(e =>
      e.presetName === presetName && (e.state === 'requested' || e.state === 'receiving'));
  }

  private executingCount(): number
  {
    return this.entriesSignal().filter(e => e.state === 'requested' || e.state === 'receiving').length;
  }

  private find(id: number): DownloadEntry | undefined
  {
    return this.entriesSignal().find(e => e.id === id);
  }

  //
  // Every state change funnels through here, which is why the outcome is reported here: a single
  // place that cannot be forgotten when a new transition is added, rather than one call per call
  // site. Only a terminal state is worth an event - the intermediate ones are progress, and a
  // 'receiving' patch arrives once per chunk.
  //
  // The native route is the gap, and it is not closable from this side: it reaches its terminal
  // state 'unknown' at creation and is never patched again, because a submit into a hidden iframe
  // tells the page nothing about what became of it. Those downloads therefore report a start and no
  // outcome, which is honest - inventing a completion for them would be worse than the hole.
  //
  private patch(id: number, changes: Partial<DownloadEntry>): void
  {
    this.entriesSignal.update(entries => entries.map(e => e.id === id ? { ...e, ...changes } : e));

    if (changes.state && isTerminal(changes.state))
    {
      const entry = this.find(id);
      if (entry)
      {
        const outcome = changes.state === 'received' ? 'Download received'
          : changes.state === 'failed' ? 'Download failed'
          : changes.state === 'cancelled' ? 'Download cancelled'
          : 'Download submitted';

        //
        // The HTTP status goes into the event name for a failure, because "which reports fail" and
        // "how they fail" are the same question in practice - a 401 that means the session lapsed is
        // a different problem from a 500 out of the report itself.
        //
        const name = changes.state === 'failed' && entry.failure
          ? `${entry.presetName} (${entry.failure.status})`
          : entry.presetName;

        this.thirdParty.trackEvent('Report', outcome, name);
      }
    }
  }
}

//
// A URL without its query, for comparing where an answer came from with where it was asked for. The
// query is dropped because a backend is free to normalise it and that is not a bounce.
//
function stripQuery(url: string): string
{
  const q = url.indexOf('?');
  return q < 0 ? url : url.slice(0, q);
}

//
// Content types whose answer is a document to be read rather than a file to be saved. Deliberately a
// list of what IS textual rather than of what is binary: a new binary format should be delivered, and
// only a text format needs the browser to render it.
//
function isTextualType(contentType: string): boolean
{
  return /^\s*(text\/|application\/(json|xml|xhtml))/i.test(contentType);
}

//
// The first bytes of the answer as text. An XLSX and a ZIP begin `PK`, a PDF `%PDF`; nothing that is
// meant to be saved begins with `<` or `{`.
//
async function readOpening(blob: Blob): Promise<string>
{
  const head = blob.slice(0, 512);
  try
  {
    return await head.text();
  }
  catch
  {
    return '';
  }
}

function looksLikeDocument(opening: string): boolean
{
  const first = opening.replace(/^\uFEFF/, '').trimStart().charAt(0);
  return first === '<' || first === '{';
}

//
// Whatever fetch rejected with, in one line. It is nearly always a TypeError whose message is the
// browser's own words for "the request did not complete" - "Failed to fetch" in Chromium, "Load
// failed" in Safari, "NetworkError when attempting to fetch resource" in Firefox - and those words
// are the only clue there is, so they are kept verbatim rather than replaced by one of ours.
//
function describe(error: unknown): string
{
  if (error instanceof Error)
  {
    return error.name === 'Error' ? error.message : error.name + ': ' + error.message;
  }
  return String(error);
}

export function isTerminal(state: DownloadState): boolean
{
  return TERMINAL_STATES.includes(state);
}

//
// A report posts a file when one of its parameters is of data type F, which gmr-functions.sql maps to
// data_ind_html_control = 'file'. Read off the form rather than off the catalogue row: the form is
// what is actually being submitted.
//
function isFileUpload(fn: GmrFunction): boolean
{
  return (fn.cfn_form_encoding ?? '').toLowerCase().includes('multipart');
}

//
// RFC 6266: filename* wins over filename when both are present.
//
function fileNameFrom(disposition: string): string | null
{
  const extended = /filename\*\s*=\s*[^']*'[^']*'([^;]+)/i.exec(disposition);
  if (extended)
  {
    try { return decodeURIComponent(extended[1].trim()); } catch { /* fall through to the plain form */ }
  }

  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(disposition);
  return plain ? plain[1].trim() : null;
}
